import * as Styles from './styles';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';
import {
  BUTTON_VARIANTS,
  Divider,
  IconClose,
} from 'cdk-radial';
import { useHistory } from 'react-router-dom';
import { useWebViewStore } from '../webview/useWebViewStore';
import launchExternal from '../../util/launchExternal';
import { AdoptEaseCampaignAction, IAdoptEaseCampaign } from './AdoptEaseCampaignInterface';
import { getCampaignText } from './adoptEaseCampaignUtils';
import userProfileStore from '../core/UserProfileStore';

import { Button } from './../../components/Buttons/Button';

interface AdoptEaseCampaignDialogProps {
  isOpen: boolean;
  onClose: () => void;
  iconColor: string;
  iconText: string;
  name: string;
  id: string;
  campaign?: IAdoptEaseCampaign;
}

export default function AdoptEaseCampaignDialog({
  isOpen,
  onClose,
  iconColor,
  iconText,
  name,
  id,
  campaign,
}: AdoptEaseCampaignDialogProps) {

  const { trackEvent } = useAnalytics();
  const { t } = useTranslation();
  const history = useHistory();
  const { setCustomWebView } = useWebViewStore();
  const locale = userProfileStore((state) => state.locale);

  const ctaUrl = campaign?.ctaAction?.url || '';
  const cdkuUrl = campaign?.cdkuUrl || '';
  const surveyUrl = campaign?.surveyUrl || '';

  const launchUrl = (url: string) => {
    if (!launchExternal(url)) {
      history.push(`/unify/view/${id}/cta`);
      setCustomWebView({ id, url });
    }
  };

  const trackCampaignAction = (action: AdoptEaseCampaignAction) => {
    trackEvent(AMPLITUDE_EVENTS.ADOPTEASE_CAMPAIGN, { Action: action, Item: id });
  };

  // Survey opens in a new tab so the user stays on the current page
  const handleDismiss = () => {
    if (surveyUrl && !launchExternal(surveyUrl)) {
      window.open(surveyUrl, '_blank', 'noopener,noreferrer');
    }
    trackCampaignAction(AdoptEaseCampaignAction.DISMISS);
    onClose();
  };

  const handleOpen = () => {
    onClose();
    trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION, { Application: name });
    trackCampaignAction(AdoptEaseCampaignAction.OPEN_WORKFLOW);
    launchUrl(ctaUrl);
  };

  const handleTraining = () => {
    onClose();
    trackCampaignAction(AdoptEaseCampaignAction.TAKE_TRAINING);
    launchUrl(cdkuUrl);
  };

  return (
    <Styles.StyledApplicationDialog
      isOpen={isOpen}
      id="campaign-dialog"
      data-testid="adoptease-campaign-dialog"
      onClose={handleDismiss}
      hideHeader
      hideCloseButton={true}
      disableOverlayClicked={true}
      title=''
    >
      <Styles.StyledDiv style={{ padding: '10px 16px 16px 5px', justifyContent: 'space-between', height: '70px' }}>
        <Styles.CardHeader>
          <Styles.ProductIcon iconColor={iconColor} data-testid="app-icon">
            {iconText || ''}
          </Styles.ProductIcon>
          <div style={{ paddingLeft: '10px' }}>
            <Styles.CardTitle>{name}</Styles.CardTitle>
            <div style={{ fontWeight: '400' }}>CDK Global</div>
          </div>
        </Styles.CardHeader>
        <Styles.RoundedIconButton
          data-testid="campaign-on-close-icon"
          size="small"
          icon={<IconClose />}
          onClick={handleDismiss}
          text="BannerClose"
        />
      </Styles.StyledDiv>

      <Divider data-testid="header-divider" />

      <div style={{ paddingTop: '15px', fontSize: '20px', fontWeight: '500' }} data-testid="campaign-title">
        {getCampaignText(campaign?.title, locale)}
      </div>

      <div style={{ paddingTop: '5px', fontSize: '16px', fontWeight: '350' }} data-testid="campaign-description">
        {getCampaignText(campaign?.description, locale)}
      </div>

      <Styles.StyledAllButtonDiv>
        <Styles.StyledButtonDiv>
          <Styles.StyledCancelButton style={{color: '#155EBF'}}
            text={t('adoptease.dismiss')}
            data-testid="campaign-dismiss-button"
            onClick={handleDismiss}
          />
          {ctaUrl && (
            <Button
              style={{ backgroundColor: '#E8F1FC', color: '#155EBF', fontSize: '14px', fontWeight: '500', marginBottom: '10px', marginRight: '10px', display: 'grid' }}
              variant={BUTTON_VARIANTS.PRIMARY}
              text={getCampaignText(campaign?.openButtonText, locale) || `${t('adoptease.open')}  ${name}`}
              data-testid="campaign-open-button"
              onClick={handleOpen}
            />
          )}
          {cdkuUrl && (
            <Button
              style={{ backgroundColor: '#196FE1', fontSize: '14px', fontWeight: '500' }}
              variant={BUTTON_VARIANTS.PRIMARY}
              text={t('adoptease.take')}
              data-testid="campaign-training-button"
              onClick={handleTraining}
            />
          )}
        </Styles.StyledButtonDiv>
      </Styles.StyledAllButtonDiv>
    </Styles.StyledApplicationDialog>
  );
}
